// ============================================================
//  INSURER POLICIES PAGE — ADMIN DASHBOARD
//  Path: src/pages/InsurerPage.tsx
//
//  The per-job policies bought from the connected insurer (XCover or any
//  other company — nothing here names one). Shows whether the insurer is
//  on, and lets an admin retry a policy that failed to buy.
//  Prices and statuses come from the backend; this page only displays them.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { getInsurerStatus, listInsurerPolicies, retryInsurerPolicy } from '../services/admin.service'

interface PolicyRow {
  id: string
  taskId: string
  insuranceType: string
  provider: string
  providerPolicyId: string | null
  price: number
  currency: string
  status: 'quoted' | 'binding' | 'active' | 'cancelled' | 'failed'
  certificateUrl: string | null
  attempts: number
  lastError: string | null
  priceDrift: number | null
  createdAt: string
}
interface InsurerStatus { enabled: boolean; provider: string; configured: boolean; bindAt: 'start' | 'on_way' | 'hire' }

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'failed', label: 'Failed' },
  { value: 'quoted', label: 'Not bought yet' },
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
]
const STATUS_COLORS: Record<string, string> = {
  quoted: 'bg-yellow-600/20 text-yellow-400',
  binding: 'bg-yellow-600/20 text-yellow-400',
  active: 'bg-green-600/20 text-green-400',
  cancelled: 'bg-gray-600/20 text-gray-400',
  failed: 'bg-red-600/20 text-red-400',
}
const STATUS_LABELS: Record<string, string> = {
  quoted: 'Not bought yet', binding: 'Buying…', active: 'Active', cancelled: 'Cancelled', failed: 'Failed',
}
const PAGE_SIZE = 20

const InsurerPage: React.FC = () => {
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<PolicyRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [info, setInfo] = useState<InsurerStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [s, p] = await Promise.all([
        getInsurerStatus(),
        listInsurerPolicies({ page, limit: PAGE_SIZE, status: status || undefined }),
      ])
      setInfo(s.data.data)
      setRows(p.data.data)
      setTotal(p.data.pagination.total)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load insurer policies')
    } finally {
      setLoading(false)
    }
  }, [page, status])

  useEffect(() => { fetchRows() }, [fetchRows])

  const retry = async (id: string) => {
    setBusyId(id)
    setError('')
    try {
      await retryInsurerPolicy(id)
      await fetchRows()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Retry failed')
    } finally {
      setBusyId(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-white">Insurer policies</h1>
        <p className="text-gray-400 text-sm mt-1">{total} polic{total === 1 ? 'y' : 'ies'}</p>
      </div>

      {info && (
        <div className={`mb-4 inline-block rounded px-3 py-2 text-sm ${info.enabled && info.configured ? 'bg-green-600/20 text-green-400' : 'bg-gray-700 text-gray-300'}`}>
          {info.enabled
            ? (info.configured
                ? `Insurer ON — ${info.provider} (policy bought ${info.bindAt === 'hire' ? 'at hire' : info.bindAt === 'on_way' ? 'when the tasker taps On my way' : 'at arrival / job start'})`
                : `Insurer switched on but "${info.provider}" is not set up — manual fees are used`)
            : 'Insurer OFF — manual fees are used everywhere'}
        </div>
      )}
      <p className="text-xs text-gray-500 mb-6 max-w-3xl">
        A policy is created per job for categories set to Insurer pricing. A failed purchase is retried
        automatically; the customer and tasker are never blocked. Retry here to try again now.
      </p>

      <div className="flex gap-3 mb-6">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1) }}
          className="bg-gray-800 text-white rounded px-3 py-2 text-sm"
        >
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {error && <div className="mb-4 text-sm text-red-400">{error}</div>}

      <div className="bg-gray-800 rounded-lg overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-400 uppercase">
            <tr>
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Insurer</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Policy</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-gray-400">No policies</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-700 align-top">
                <td className="px-4 py-3"><span className="text-gray-300" title={r.taskId}>{r.taskId}</span></td>
                <td className="px-4 py-3 text-gray-300">{r.insuranceType}</td>
                <td className="px-4 py-3 text-gray-300">{r.provider}</td>
                <td className="px-4 py-3 text-gray-300">
                  ${r.price.toFixed(2)} {r.currency}
                  {r.priceDrift ? <div className="text-xs text-orange-400">Price changed by ${r.priceDrift.toFixed(2)} at purchase</div> : null}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[r.status] || ''}`}>{STATUS_LABELS[r.status] || r.status}</span>
                  {r.lastError && <div className="text-xs text-red-400 mt-1 max-w-xs break-words">{r.lastError}</div>}
                  {r.status === 'failed' && <div className="text-xs text-gray-500">Attempts: {r.attempts}</div>}
                </td>
                <td className="px-4 py-3 text-gray-300">
                  {r.providerPolicyId || '—'}
                  {r.certificateUrl && <div><a className="text-blue-400 text-xs" href={r.certificateUrl} target="_blank" rel="noreferrer">Certificate</a></div>}
                </td>
                <td className="px-4 py-3">
                  {(r.status === 'failed' || r.status === 'quoted') && (
                    <button
                      onClick={() => retry(r.id)}
                      disabled={busyId === r.id}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-3 py-1 text-xs"
                    >
                      {busyId === r.id ? 'Trying…' : 'Retry'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4 text-sm text-gray-400">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 bg-gray-800 rounded disabled:opacity-40">Previous</button>
        <span>Page {page} of {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 bg-gray-800 rounded disabled:opacity-40">Next</button>
      </div>
    </div>
  )
}

export default InsurerPage